// Shared modals for the Post-Settlement Command Centre.
// Used by both the client list quick-actions and the client profile page.
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#0057FF';

const HANDOFF_REASONS = [
  { key: 'rate_review',        label: 'Rate review' },
  { key: 'refinance',          label: 'Refinance' },
  { key: 'debt_consolidation', label: 'Debt consolidation' },
  { key: 'equity_release',     label: 'Equity release' },
  { key: 'purchase',           label: 'Purchase another property' },
  { key: 'investment',         label: 'Investment property' },
  { key: 'construction',       label: 'Construction' },
  { key: 'fixed_expiry',       label: 'Fixed rate expiry' },
  { key: 'unhappy_lender',     label: 'Client unhappy with lender' },
  { key: 'client_wants_broker',label: 'Client wants to speak to broker' },
  { key: 'other',              label: 'Other' },
];

export const QuickNoteModal = ({ loanId, authHeaders, onClose, onSaved }) => {
  const [form, setForm] = useState({
    contacted: true, channel: 'call',
    happy_with_loan: null, concerns: '',
    buying_again: false, refinancing: false, personal_debts: false,
    rate_review_interest: false, referral_potential: false,
    send_to_broker: false, next_followup_date: '', notes: '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/post-settlement/clients/${loanId}/quick-note`, form, { headers: authHeaders });
      toast.success('Check-in logged');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center backdrop-blur-sm p-0 sm:p-4"
      style={{ backgroundColor: 'rgba(2,6,23,0.6)' }}
      onClick={onClose}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} transition={{ type: 'spring', stiffness: 260, damping: 26 }}
        className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="ps-quick-note-modal">
        <div className="sticky top-0 px-6 py-5 flex items-center justify-between bg-white/95 backdrop-blur border-b border-slate-100 z-10">
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Post-settlement</span>
            <p className="text-xl font-black text-slate-900">Log check-in</p>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <ToggleField label="Contacted?" v={form.contacted} onChange={(v) => setForm({ ...form, contacted: v })} testid="ps-note-contacted" />
            <ChannelSelect v={form.channel} onChange={(v) => setForm({ ...form, channel: v })} />
          </div>
          <TriToggle label="Happy with loan?" v={form.happy_with_loan} onChange={(v) => setForm({ ...form, happy_with_loan: v })} />
          <TextField label="Concerns raised" v={form.concerns} onChange={(v) => setForm({ ...form, concerns: v })} placeholder="Any concerns…" />
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Opportunity flags</span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <FlagCheck label="Buying again" v={form.buying_again} onChange={(v) => setForm({ ...form, buying_again: v })} />
              <FlagCheck label="Refinancing" v={form.refinancing} onChange={(v) => setForm({ ...form, refinancing: v })} />
              <FlagCheck label="Personal debts" v={form.personal_debts} onChange={(v) => setForm({ ...form, personal_debts: v })} />
              <FlagCheck label="Rate review" v={form.rate_review_interest} onChange={(v) => setForm({ ...form, rate_review_interest: v })} />
              <FlagCheck label="Referral potential" v={form.referral_potential} onChange={(v) => setForm({ ...form, referral_potential: v })} />
              <FlagCheck label="→ Send to broker" v={form.send_to_broker} onChange={(v) => setForm({ ...form, send_to_broker: v })} />
            </div>
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Next follow-up date</span>
            <input type="date" value={form.next_followup_date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })}
                   data-testid="ps-note-next-followup"
                   className="mt-1.5 w-full px-3 py-3 rounded-2xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
          </div>
          <TextField label="General notes" v={form.notes} onChange={(v) => setForm({ ...form, notes: v })} placeholder="Anything else worth capturing…" multiline />

          <button type="button" onClick={save} disabled={busy} data-testid="ps-note-save"
                  className="w-full rounded-full text-sm font-black py-3.5 text-white shadow-sm hover:opacity-90 disabled:opacity-60 transition-opacity"
                  style={{ background: 'linear-gradient(135deg,#0057FF 0%,#0044cc 100%)' }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save check-in'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const BrokerHandoffModal = ({ loanId, authHeaders, onClose, onSaved }) => {
  const [reason, setReason] = useState('rate_review');
  const [notes, setNotes] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/post-settlement/clients/${loanId}/broker-handoff`, {
        reason, notes: notes.trim() || null,
        estimated_amount: amount ? parseFloat(amount) : null,
      }, { headers: authHeaders });
      toast.success('Sent to broker review');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center backdrop-blur-sm p-0 sm:p-4"
      style={{ backgroundColor: 'rgba(2,6,23,0.6)' }}
      onClick={onClose}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} transition={{ type: 'spring', stiffness: 260, damping: 26 }}
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="ps-handoff-modal">
        <div className="sticky top-0 px-6 py-5 flex items-center justify-between bg-white/95 backdrop-blur border-b border-slate-100 z-10">
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Broker handoff</span>
            <p className="text-xl font-black text-slate-900">Send to broker</p>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Reason *</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
                    data-testid="ps-handoff-reason"
                    className="mt-1.5 w-full px-3 py-3 rounded-2xl text-sm bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300">
              {HANDOFF_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Estimated amount ($)</span>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                   placeholder="e.g. 650000" data-testid="ps-handoff-amount"
                   className="mt-1.5 w-full px-3 py-3 rounded-2xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Notes for the broker</span>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Context — what triggered this handoff, client goals, urgency…"
                      data-testid="ps-handoff-notes"
                      className="mt-1.5 w-full px-3 py-3 rounded-2xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
          </div>
          <button type="button" onClick={save} disabled={busy} data-testid="ps-handoff-submit"
                  className="w-full rounded-full text-sm font-black py-3.5 text-white shadow-sm hover:opacity-90 disabled:opacity-60 transition-opacity"
                  style={{ background: 'linear-gradient(135deg,#0057FF 0%,#0044cc 100%)' }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (<><Send className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Send to broker</>)}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Small helpers used by the modals ─────────────────────────
const ToggleField = ({ label, v, onChange, testid }) => (
  <div>
    <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</span>
    <div className="mt-1.5 flex gap-1.5">
      {[{ k: true, l: 'Yes' }, { k: false, l: 'No' }].map((o) => (
        <button key={String(o.k)} type="button" onClick={() => onChange(o.k)} data-testid={`${testid}-${o.l.toLowerCase()}`}
                className={`flex-1 rounded-xl text-xs font-black py-2.5 transition-colors ${v === o.k ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                style={v === o.k ? { background: 'linear-gradient(135deg,#0057FF 0%,#0044cc 100%)' } : {}}>
          {o.l}
        </button>
      ))}
    </div>
  </div>
);
const ChannelSelect = ({ v, onChange }) => (
  <div>
    <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">How?</span>
    <select value={v} onChange={(e) => onChange(e.target.value)} data-testid="ps-note-channel"
            className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300">
      <option value="call">Call</option>
      <option value="sms">SMS</option>
      <option value="email">Email</option>
      <option value="video">Video call</option>
      <option value="note">Just a note</option>
    </select>
  </div>
);
const TriToggle = ({ label, v, onChange }) => (
  <div>
    <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</span>
    <div className="mt-1.5 flex gap-1.5">
      {[{ k: true, l: 'Yes' }, { k: false, l: 'No' }, { k: null, l: 'Not asked' }].map((o) => (
        <button key={String(o.k)} type="button" onClick={() => onChange(o.k)}
                className={`flex-1 rounded-xl text-xs font-black py-2.5 transition-colors ${v === o.k ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                style={v === o.k ? { background: 'linear-gradient(135deg,#0057FF 0%,#0044cc 100%)' } : {}}>
          {o.l}
        </button>
      ))}
    </div>
  </div>
);
const FlagCheck = ({ label, v, onChange }) => (
  <label className={`rounded-xl p-3 border flex items-center gap-2 cursor-pointer transition-colors ${v ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}>
    <input type="checkbox" checked={v} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: BRAND }} />
    <span className={`text-xs font-black ${v ? 'text-blue-800' : 'text-slate-700'}`}>{label}</span>
  </label>
);
const TextField = ({ label, v, onChange, placeholder, multiline }) => (
  <div>
    <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</span>
    {multiline ? (
      <textarea rows={3} value={v} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                className="mt-1.5 w-full px-3 py-3 rounded-2xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
    ) : (
      <input type="text" value={v} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="mt-1.5 w-full px-3 py-3 rounded-2xl text-sm outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100" />
    )}
  </div>
);
